import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Settings, TrendingDown, FileText, PoundSterling } from 'lucide-react';
import type { Asset } from '@/types/asset';
import type { SystemYear, NominalAccount } from '@/types/financials';
import { useState, useEffect } from 'react';
import { dataService } from '@/services/data-service';
import { ProjectPicker } from './ProjectPicker';
import { CostCentrePicker } from './CostCentrePicker';
import { NominalPicker } from './NominalPicker';
import { useIntegration } from '@/providers/IntegrationProvider';

interface FetchState<T> {
  data: T;
  loading: boolean;
  error: boolean;
}

function useFetchData<T>(
  fetchFn: () => Promise<T>,
  defaultValue: T
): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: defaultValue,
    loading: false,
    error: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      setState(prev => ({ ...prev, loading: true, error: false }));
      try {
        const data = await fetchFn();
        setState({ data, loading: false, error: false });
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setState({ data: defaultValue, loading: false, error: true });
      }
    };
    fetchData();
  }, []);

  return state;
}

interface YearSelectorProps {
  readonly id: string;
  readonly label: string;
  readonly value: number | undefined;
  readonly onChange: (value: number) => void;
  readonly systemYears: SystemYear[];
  readonly loading: boolean;
  readonly error: boolean;
  readonly isEditMode: boolean;
}

// Helper function to render year selector content - extracted to reduce cognitive complexity (S3776)
function YearSelectorContent({ loading, error, systemYears }: Readonly<{ loading: boolean; error: boolean; systemYears: SystemYear[] }>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="ml-2 text-sm">Loading...</span>
      </div>
    );
  }
  if (error) {
    return <div className="p-2 text-sm text-red-500">Failed to load years</div>;
  }
  if (systemYears.length === 0) {
    return <div className="p-2 text-sm text-muted-foreground">No years available</div>;
  }
  return (
    <>
      {systemYears.map((year) => (
        <SelectItem key={year.id} value={year.nominal.toString()}>
          {year.label}
        </SelectItem>
      ))}
    </>
  );
}

function YearSelector({ id, label, value, onChange, systemYears, loading, error, isEditMode }: Readonly<YearSelectorProps>) {
  const displayValue = systemYears.find(y => y.nominal === value)?.label ?? value?.toString() ?? '';
  
  if (!isEditMode) {
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} value={displayValue} readOnly />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value?.toString() ?? ''}
        onValueChange={(val: string) => onChange(parseInt(val))}
        disabled={loading}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={loading ? "Loading years..." : "Select year"} />
        </SelectTrigger>
        <SelectContent>
          <YearSelectorContent loading={loading} error={error} systemYears={systemYears} />
        </SelectContent>
      </Select>
    </div>
  );
}

interface AssetDetailSettingsProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
  readonly errors?: Record<string, string>;
}

// Helper to check if a field should be shown based on depreciation method - reduces cognitive complexity (S3776)
function shouldShowField(depreciationMethod: string | undefined, field: 'frequency' | 'percentage' | 'years' | 'inService' | 'postings' | 'baseValue'): boolean {
  const method = depreciationMethod ?? 'Straight Line';
  switch (field) {
    case 'frequency':
    case 'percentage':
      return method !== 'Sum of Years Digits';
    case 'years':
      return method !== 'Declining Balance';
    case 'inService':
      return method === 'Straight Line' || method === 'None';
    case 'postings':
      return method !== 'Declining Balance';
    case 'baseValue':
      return method === 'Straight Line' || method === 'Period Ratio' || method === 'None';
    default:
      return true;
  }
}

// Helper component for editable select field - reduces cognitive complexity (S3776)
function EditableSelectField({ 
  id, 
  label, 
  value, 
  options, 
  onChange, 
  isEditMode 
}: Readonly<{
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  isEditMode: boolean;
}>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {isEditMode ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input id={id} value={options.find(o => o.value === value)?.label ?? value} readOnly />
      )}
    </div>
  );
}

// Helper component for number input field - reduces cognitive complexity (S3776)
function NumberInputField({
  id,
  label,
  value,
  onChange,
  isEditMode,
  step,
  parseValue = parseInt
}: Readonly<{
  id: string;
  label: string;
  value: number | string | undefined;
  onChange: (value: number) => void;
  isEditMode: boolean;
  step?: string;
  parseValue?: (value: string) => number;
}>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(parseValue(e.target.value))}
        readOnly={!isEditMode}
        className={!isEditMode ? 'bg-muted' : ''}
      />
    </div>
  );
}

// Helper component for nominal picker field with error handling - reduces cognitive complexity (S3776)
function NominalPickerField({
  id,
  label,
  value,
  displayValue,
  onValueChange,
  nominalAccounts,
  loading,
  error,
  isEditMode,
  fieldError
}: Readonly<{
  id: string;
  label: string;
  value: string | undefined;
  displayValue: string | undefined;
  onValueChange: (code: string, name: string) => void;
  nominalAccounts: NominalAccount[];
  loading: boolean;
  error: boolean;
  isEditMode: boolean;
  fieldError?: string;
}>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {isEditMode && <span className="text-destructive ml-1">*</span>}
      </Label>
      <NominalPicker
        value={value}
        displayValue={displayValue}
        onValueChange={onValueChange}
        nominalAccounts={nominalAccounts}
        loading={loading}
        error={error}
        disabled={!isEditMode}
        placeholder="Select nominal account..."
        className={fieldError ? 'border-destructive' : ''}
      />
      {fieldError && (
        <p className="text-sm text-destructive">{fieldError}</p>
      )}
    </div>
  );
}

export function AssetDetailSettings({ asset, onAssetUpdate, isEditMode = false, errors = {} }: Readonly<AssetDetailSettingsProps>) {
  const { settings: integrationSettings } = useIntegration();
  const hasFinancialsIntegration = integrationSettings?.enabled && 
    integrationSettings?.integrationType === 'access-financials';
  
  const systemYearsState = useFetchData(
    () => dataService.getSystemYears('desc'),
    [] as SystemYear[]
  );
  
  const nominalAccountsState = useFetchData(
    async () => {
      const data = await dataService.getNominalAccounts();
      return data.filter((account: NominalAccount) => !account.doNotUse);
    },
    [] as NominalAccount[]
  );

  const systemYears = systemYearsState.data;
  const systemYearsLoading = systemYearsState.loading;
  const systemYearsError = systemYearsState.error;
  const nominalAccounts = nominalAccountsState.data;
  const nominalAccountsLoading = nominalAccountsState.loading;
  const nominalAccountsError = nominalAccountsState.error;

  const getFrequencyValue = (frequency: string | undefined): number => {
    const frequencyMap: Record<string, number> = {
      'Monthly': 12,
      'Quarterly': 4,
      'Half Yearly': 2,
      'Yearly': 1,
    };
    return frequencyMap[frequency ?? ''] ?? 12;
  };

  const handleMethodChange = (updated: Asset, method: string): void => {
    if (method === 'Sum of Digits') {
      updated.postingFrequency = 'Monthly';
      if (updated.depreciationYears) {
        updated.depreciationYears = Math.round(updated.depreciationYears);
        updated.totalPostings = updated.depreciationYears * 12;
      }
    } else if (method === 'Reducing Balance' || method === 'None') {
      updated.depreciationYears = 0;
      updated.totalPostings = 0;
      updated.baseValueOn = 'original';
    }
  };

  const handleYearsChange = (updated: Asset, value: number): void => {
    const years = value ?? 0;
    const freq = getFrequencyValue(updated.postingFrequency);
    updated.totalPostings = years * freq;
    if (years > 0) updated.depreciationPercentage = 100 / years;
    if (updated.depreciationMethod === 'Sum of Digits' && years > 0) {
      updated.depreciationYears = Math.round(years);
      updated.totalPostings = Math.round(years) * freq;
    }
  };

  const handlePercentageChange = (updated: Asset, value: number): void => {
    const percentage = value ?? 0;
    if (percentage > 0) {
      const freq = getFrequencyValue(updated.postingFrequency);
      const years = 100 / percentage;
      updated.depreciationYears = years;
      updated.totalPostings = years * freq;
      updated.depreciationPercentage = 100 / years;
    }
  };

  const applyDepreciationAutoCalc = (updated: Asset, field: keyof Asset, value: any): void => {
    if (field === 'depreciationMethod') handleMethodChange(updated, value);
    else if (field === 'postingFrequency') updated.totalPostings = (updated.depreciationYears ?? 0) * getFrequencyValue(value);
    else if (field === 'depreciationYears') handleYearsChange(updated, value);
    else if (field === 'depreciationPercentage') handlePercentageChange(updated, value);
  };

  const handleFieldChange = (field: keyof Asset, value: any) => {
    const updated = { ...asset, [field]: value };
    applyDepreciationAutoCalc(updated, field, value);
    onAssetUpdate(updated);
  };

  return (
    <div className="space-y-6">
      {/* Depreciation Settings Card */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Depreciation Settings
          </CardTitle>
          <CardDescription className="text-xs">
            Configure depreciation method, frequency, and posting schedule
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          {/* Row 1: Method, Frequency, Percentage, Years */}
          <div className="space-y-2">
            <Label htmlFor="depreciationMethod">Depreciation Method</Label>
            {isEditMode ? (
              <Select
                value={asset.depreciationMethod ?? 'Straight Line'}
                onValueChange={(value: string) => handleFieldChange('depreciationMethod', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Straight Line">Straight Line</SelectItem>
                  <SelectItem value="Reducing Balance">Reducing Balance</SelectItem>
                  <SelectItem value="Sum of Digits">Sum of Digits</SelectItem>
                  <SelectItem value="Period Ratio">Period Ratio</SelectItem>
                  <SelectItem value="None">None</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="depreciationMethod"
                value={asset.depreciationMethod ?? 'Straight Line'}
                readOnly
              />
            )}
          </div>
          
          {/* Hide Frequency when method is Sum of Digits */}
          {asset.depreciationMethod !== 'Sum of Digits' && (
            <div className="space-y-2">
              <Label htmlFor="postingFrequency">Posting Frequency</Label>
              {isEditMode ? (
                <Select
                  value={asset.postingFrequency ?? 'Monthly'}
                  onValueChange={(value: string) => handleFieldChange('postingFrequency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Half Yearly">Half Yearly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="postingFrequency"
                  value={asset.postingFrequency ?? 'Monthly'}
                  readOnly
                />
              )}
            </div>
          )}
          
          {/* Hide Percentage when method is Sum of Digits */}
          {asset.depreciationMethod !== 'Sum of Digits' && (
            <div className="space-y-2">
              <Label htmlFor="depreciationPercentage">Percentage</Label>
              <Input
                id="depreciationPercentage"
                type="number"
                step="0.01"
                value={asset.depreciationPercentage ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('depreciationPercentage', parseFloat(e.target.value) ?? undefined)}
                readOnly={!isEditMode}
                className={!isEditMode ? 'bg-muted' : ''}
              />
            </div>
          )}
          
          {/* Hide Years when method is Reducing Balance */}
          {asset.depreciationMethod !== 'Reducing Balance' && (
            <div className="space-y-2">
              <Label htmlFor="depreciationYears">Years</Label>
              <Input
                id="depreciationYears"
                type="number"
                value={(asset.depreciationYears || asset.usefulLife) ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('depreciationYears', parseInt(e.target.value) ?? undefined)}
                readOnly={!isEditMode}
                className={!isEditMode ? 'bg-muted' : ''}
              />
            </div>
          )}
          
          {/* Row 2: In Service Period, In Service Year, Next Posting Period, Next Posting Year */}
          
          {/* Show In Service Period/Year only for Straight Line or None */}
          {(asset.depreciationMethod === 'Straight Line' || asset.depreciationMethod === 'None') && (
            <>
              <div className="space-y-2">
                <Label htmlFor="inServicePeriod">In Service Period</Label>
                <Input
                  id="inServicePeriod"
                  type="number"
                  value={asset.inServicePeriod ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('inServicePeriod', parseInt(e.target.value) ?? undefined)}
                  readOnly={!isEditMode}
                  className={!isEditMode ? 'bg-muted' : ''}
                />
              </div>
              <YearSelector
                id="inServiceYear"
                label="In Service Year"
                value={asset.inServiceYear}
                onChange={(value) => handleFieldChange('inServiceYear', value)}
                systemYears={systemYears}
                loading={systemYearsLoading}
                error={systemYearsError}
                isEditMode={isEditMode}
              />
            </>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="nextPostingPeriod">Next Posting Period</Label>
            <Input
              id="nextPostingPeriod"
              type="number"
              value={asset.nextPostingPeriod ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('nextPostingPeriod', parseInt(e.target.value) ?? undefined)}
              readOnly={!isEditMode}
              className={!isEditMode ? 'bg-muted' : ''}
            />
          </div>
          <YearSelector
            id="nextPostingYear"
            label="Next Posting Year"
            value={asset.nextPostingYear}
            onChange={(value) => handleFieldChange('nextPostingYear', value)}
            systemYears={systemYears}
            loading={systemYearsLoading}
            error={systemYearsError}
            isEditMode={isEditMode}
          />
          
          {/* Row 3: Postings Made, Total Postings */}
          
          {/* Hide Postings Made/Total when method is Reducing Balance */}
          {asset.depreciationMethod !== 'Reducing Balance' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="postingsMade">Postings Made</Label>
                <Input
                  id="postingsMade"
                  type="number"
                  value={asset.postingsMade ?? 0}
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totalPostings">Total Postings</Label>
                <Input
                  id="totalPostings"
                  type="number"
                  value={asset.totalPostings ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('totalPostings', parseInt(e.target.value) ?? undefined)}
                  readOnly={!isEditMode}
                  className={!isEditMode ? 'bg-muted' : ''}
                />
              </div>
            </>
          )}
          
          {/* Row 4: Do Not Depreciate toggle */}
          <div className="space-y-2 md:col-span-4">
            <Label htmlFor="doNotDepreciate">Do Not Depreciate</Label>
            <div className="flex items-center gap-2">
              <Switch
                id="doNotDepreciate"
                checked={asset.doNotDepreciate ?? false}
                onCheckedChange={(checked) => handleFieldChange('doNotDepreciate', checked)}
                disabled={!isEditMode}
              />
              <span className="text-sm text-muted-foreground">
                {asset.doNotDepreciate ? 'Enabled' : 'Disabled'}
              </span>
            </div>
        </div>
        </CardContent>
      </Card>

      {/* Show Base Value card only for Straight Line, Period Ratio, or None */}
      {(asset.depreciationMethod === 'Straight Line' || 
        asset.depreciationMethod === 'Period Ratio' || 
        asset.depreciationMethod === 'None') && (
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Base Value
            </CardTitle>
            <CardDescription className="text-xs">
              Base value calculation method and current value
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="baseValueOn">Base Value On</Label>
                {isEditMode ? (
                  <Select
                    value={asset.baseValueOn ?? 'remaining'}
                    onValueChange={(value: string) => handleFieldChange('baseValueOn', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remaining">Remaining Postings</SelectItem>
                      <SelectItem value="original">Original Postings</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="baseValueOn"
                    value={asset.baseValueOn === 'original' ? 'Original Postings' : 'Remaining Postings'}
                    readOnly
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="currentBaseValue">Current Base Value</Label>
                <Input
                  id="currentBaseValue"
                  type="number"
                  step="0.01"
                  value={asset.currentBaseValue ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('currentBaseValue', parseFloat(e.target.value) ?? undefined)}
                  readOnly={!isEditMode}
                  className={!isEditMode ? 'bg-muted' : ''}
                />
              </div>
          </CardContent>
        </Card>
      )}

      {/* Nominal Card */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Nominal
          </CardTitle>
          <CardDescription className="text-xs">
            Nominal account codes for asset transactions and postings
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <NominalPickerField
              id="assetCostNominal"
              label="Asset Cost Nominal"
              value={asset.assetCostNominal}
              displayValue={asset.assetCostNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, assetCostNominal: code, assetCostNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.assetCostNominal}
            />

          <div className="space-y-2">
            <Label htmlFor="depreciationBSNominal">Depreciation B/S Nominal</Label>
            <NominalPicker
              value={asset.depreciationBSNominal}
              displayValue={asset.depreciationBSNominalName}
              onValueChange={(code, name) => {
                onAssetUpdate({ ...asset, depreciationBSNominal: code, depreciationBSNominalName: name });
              }}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              disabled={!isEditMode}
              placeholder="Select nominal account..."
            />
            <NominalPickerField
              id="depreciationPLNominal"
              label="Depreciation P&L Nominal"
              value={asset.depreciationPLNominal}
              displayValue={asset.depreciationPLNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, depreciationPLNominal: code, depreciationPLNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.depreciationPLNominal}
            />
            <NominalPickerField
              id="disposalsNominal"
              label="Disposals Nominal"
              value={asset.disposalsNominal}
              displayValue={asset.disposalsNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, disposalsNominal: code, disposalsNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.disposalsNominal}
            />
            <NominalPickerField
              id="revaluationBSNominal"
              label="Revaluation B/S Nominal"
              value={asset.revaluationBSNominal}
              displayValue={asset.revaluationBSNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, revaluationBSNominal: code, revaluationBSNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.revaluationBSNominal}
            />
            <NominalPickerField
              id="revaluationPLNominal"
              label="Revaluation P&L Nominal"
              value={asset.revaluationPLNominal}
              displayValue={asset.revaluationPLNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, revaluationPLNominal: code, revaluationPLNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.revaluationPLNominal}
            />
            <NominalPickerField
              id="disposalsBankAccountNominal"
              label="Disposals Bank Account Nominal"
              value={asset.disposalsBankAccountNominal}
              displayValue={asset.disposalsBankAccountNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, disposalsBankAccountNominal: code, disposalsBankAccountNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.disposalsBankAccountNominal}
            />
            <NominalPickerField
              id="additionsWIPBankAccountNominal"
              label="Additions WIP / Bank Account Nominal"
              value={asset.additionsWIPBankAccountNominal}
              displayValue={asset.additionsWIPBankAccountNominalName}
              onValueChange={(code, name) => onAssetUpdate({ ...asset, additionsWIPBankAccountNominal: code, additionsWIPBankAccountNominalName: name })}
              nominalAccounts={nominalAccounts}
              loading={nominalAccountsLoading}
              error={nominalAccountsError}
              isEditMode={isEditMode}
              fieldError={errors.additionsWIPBankAccountNominal}
            />
          </div>
          </CardContent>
        </Card>

      {/* Costing Card */}
      {hasFinancialsIntegration && (
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PoundSterling className="h-5 w-5" />
              Costing
            </CardTitle>
            <CardDescription className="text-xs">
              Project and cost centre allocation for expense tracking
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="project">
                Project
                {isEditMode && <span className="text-destructive ml-1">*</span>}
              </Label>
              <ProjectPicker
                value={asset.project}
                displayValue={asset.projectName}
                onValueChange={(code, name) => {
                  onAssetUpdate({ ...asset, project: code, projectName: name });
                }}
                disabled={!isEditMode}
                placeholder="Select project..."
                className={errors.project ? 'border-destructive' : ''}
              />
              {errors.project && (
                <p className="text-sm text-destructive">{errors.project}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="costCentre">
                Cost Centre
                {isEditMode && <span className="text-destructive ml-1">*</span>}
              </Label>
              <CostCentrePicker
                value={asset.costCentre}
                displayValue={asset.costCentreName}
                onValueChange={(code, name) => {
                  onAssetUpdate({ ...asset, costCentre: code, costCentreName: name });
                }}
                projectCode={asset.project}
                disabled={!isEditMode}
                placeholder="Select cost centre..."
                className={errors.costCentre ? 'border-destructive' : ''}
              />
              {errors.costCentre && (
                <p className="text-sm text-destructive">{errors.costCentre}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
